import React, { useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Container,
  Row,
  Table,
  UncontrolledTooltip
} from 'reactstrap';
import SimpleHeader from 'components/Headers/SimpleHeader.js';
import { useMutation, useQuery } from '@apollo/client';
import useConfirmation from 'hooks/useConfirmation';
import useNotificationAlert from 'hooks/useNotificationAlert';
import Pagination from 'components/Pagination';
import { GET_USER_TEMPLATES } from 'graphql/queries';
import { SURVEY_USE_TEMPLATE } from 'graphql/mutations';
import { GET_GATHER_TEMPLATES } from 'graphql/queries';
import TagsFilter from './TagsFilter';
import { getSurveyUrl } from 'helpers/global';
import useWhiteLabel from 'hooks/useWhiteLabel';

const perPage = 10;
const SelectTemplate = () => {
  const [pageUserTemplates, setPageUserTemplates] = useState(0);

  const [pageGatherTemplates, setPageGatherTemplates] = useState(0);

  const {
    loading: isLoadingUserTemplates,
    data: dataUserTemplates,
    networkStatus: networkStatusUserTemplates
  } = useQuery(GET_USER_TEMPLATES, {
    variables: { first: perPage, page: pageUserTemplates },
    notifyOnnetworkStatusUserTemplatesChange: true
  });

  const {
    loading: isLoadingGatherTemplates,
    data: dataGatherTemplates,
    networkStatus: networkStatusGatherTemplates,
    refetch: refetchGatherTemplates
  } = useQuery(GET_GATHER_TEMPLATES, {
    variables: { first: perPage, page: pageGatherTemplates },
    notifyOnnetworkStatusUserTemplatesChange: true
  });

  const { show: showNotificationAlert } = useNotificationAlert({
    place: 'br'
  });

  const [surveyUseTemplate, { loading: isLoadingUseTemplate }] = useMutation(SURVEY_USE_TEMPLATE);

  const { showConfirmation, hideConfirmation } = useConfirmation(isLoadingUseTemplate);

  const [totalUserTemplatesRow, setTotalUserTemplatesRow] = useState(0);

  const [totalGatherTemplatesRow, setTotalGatherTemplatesRow] = useState(0);

  const [userTemplates, setUserTemplates] = useState();

  const [gatherTemplates, setGatherTemplates] = useState();

  useEffect(() => {
    if (dataUserTemplates && dataUserTemplates.getUserTemplates) {
      setTotalUserTemplatesRow(dataUserTemplates.getUserTemplates.paginatorInfo.total);
      setUserTemplates(JSON.parse(JSON.stringify(dataUserTemplates.getUserTemplates.data)));
    }
  }, [dataUserTemplates]);

  useEffect(() => {
    if (dataGatherTemplates && dataGatherTemplates.getGatherTemplates) {
      setTotalGatherTemplatesRow(dataGatherTemplates.getGatherTemplates.paginatorInfo.total);
      setGatherTemplates(JSON.parse(JSON.stringify(dataGatherTemplates.getGatherTemplates.data)));
    }
  }, [dataGatherTemplates]);

  const onUseTemplate = (surveyId) => {
    showConfirmation({
      title: (
        <div>
          Are you sure you want to use this template?
          <br />
          It will download to your "My Area" for use.
        </div>
      ),
      onConfirm: async () => {
        try {
          await surveyUseTemplate({ variables: { surveyId } });
          showNotificationAlert({
            title: 'Success',
            message: 'Success!'
          });
        } catch (err) {
          switch (err.message) {
            default:
              showNotificationAlert({
                type: 'danger',
                title: 'Failed',
                message: err.message
              });
              break;
          }
        } finally {
          hideConfirmation();
        }
      },
      confirmTitle: 'Use Template'
    });
  };

  const onPreviewSurvey = (surveyId, status) => {
    window.open(getSurveyUrl(surveyId, status));
  };

  const { isWhiteLabelTeam } = useWhiteLabel();

  return (
    <>
      <SimpleHeader
        name="Select Template"
        description="See the templates below. Simply select from the icons as to whether you want to: use the template or view its content."
        firstColProps={{ xs: 12 }}
        secondColProps={{ xs: 12, className: 'mt-3 mt-sm-0' }}
      />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <Card>
              <CardHeader className="border-0">
                <Row>
                  <Col>
                    <h3 className="mb-0 text-brand">My Templates</h3>
                  </Col>
                </Row>
              </CardHeader>
              {(isLoadingUserTemplates ||
                networkStatusUserTemplates === networkStatusUserTemplates.refetch) && (
                <CardBody>
                  <p className="text-center">Loading...</p>
                </CardBody>
              )}

              {!isLoadingUserTemplates &&
                networkStatusUserTemplates !== networkStatusUserTemplates.refetch &&
                dataUserTemplates?.getUserTemplates?.data.length <= 0 && (
                  <CardBody>
                    <p className="text-center">There is no data!</p>
                  </CardBody>
                )}

              {!isLoadingUserTemplates &&
                networkStatusUserTemplates !== networkStatusUserTemplates.refetch &&
                dataUserTemplates?.getUserTemplates?.data.length >= 1 && (
                  <>
                    <Table className="align-items-center table-flush" responsive striped>
                      <thead className="thead-light">
                        <tr>
                          <th>Template Name</th>
                          <th style={{ width: '200px' }}>Manage</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userTemplates?.map((data) => (
                          <tr key={`survey-item-${data.id}`}>
                            <td>{data.name}</td>
                            <td className="table-actions">
                              <div className="d-flex">
                                <button
                                  className="table-action table-action-template"
                                  id={`tooltip-template-${data.id}`}
                                  onClick={() => onUseTemplate(data.id)}>
                                  <i className="ni ni-active-40" />
                                </button>
                                <UncontrolledTooltip
                                  delay={0}
                                  target={`tooltip-template-${data.id}`}>
                                  Use this Template
                                </UncontrolledTooltip>
                                <button
                                  className="table-action table-action-preview"
                                  id={`tooltip-preview-${data.id}`}
                                  onClick={(e) => onPreviewSurvey(data.id, data.status)}>
                                  <i className="fas fa-eye" />
                                </button>
                                <UncontrolledTooltip
                                  delay={0}
                                  target={`tooltip-preview-${data.id}`}>
                                  Preview Template
                                </UncontrolledTooltip>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    <CardFooter className="py-4">
                      <Pagination
                        totalRow={totalUserTemplatesRow}
                        perPage={perPage}
                        initialPage={pageUserTemplates}
                        onPageChange={(data) => {
                          setPageUserTemplates(data.selected);
                        }}
                      />
                    </CardFooter>
                  </>
                )}
            </Card>
          </div>
        </Row>

        <Row>
          <div className="col">
            <Card>
              <CardHeader className="border-0">
                <Row>
                  <Col>
                    <h3 className="mb-0 text-brand">
                      {isWhiteLabelTeam ? 'System Templates' : 'Gather Templates'}
                    </h3>
                  </Col>
                  <Col>
                    <TagsFilter
                      isGatherTemplate={true}
                      onChange={(data) => {
                        refetchGatherTemplates({
                          tags: data.map((elm) => elm.name)
                        });
                      }}
                    />
                  </Col>
                </Row>
              </CardHeader>
              {(isLoadingGatherTemplates ||
                networkStatusGatherTemplates === networkStatusGatherTemplates.refetch) && (
                <CardBody>
                  <p className="text-center">Loading...</p>
                </CardBody>
              )}

              {!isLoadingGatherTemplates &&
                networkStatusGatherTemplates !== networkStatusGatherTemplates.refetch &&
                dataGatherTemplates?.getGatherTemplates?.data.length <= 0 && (
                  <CardBody>
                    <p className="text-center">There is no data!</p>
                  </CardBody>
                )}

              {!isLoadingGatherTemplates &&
                networkStatusGatherTemplates !== networkStatusGatherTemplates.refetch &&
                dataGatherTemplates?.getGatherTemplates?.data.length >= 1 && (
                  <>
                    <Table className="align-items-center table-flush" responsive striped>
                      <thead className="thead-light">
                        <tr>
                          <th>Template Name</th>
                          <th style={{ width: '200px' }}>Manage</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gatherTemplates?.map((data) => (
                          <tr key={`survey-item-${data.id}`}>
                            <td>{data.name}</td>
                            <td className="table-actions">
                              <div className="d-flex">
                                <button
                                  className="table-action table-action-template"
                                  id={`tooltip-template-${data.id}`}
                                  onClick={() => onUseTemplate(data.id)}>
                                  <i className="ni ni-active-40" />
                                </button>
                                <UncontrolledTooltip
                                  delay={0}
                                  target={`tooltip-template-${data.id}`}>
                                  Use this Template
                                </UncontrolledTooltip>
                                <button
                                  className="table-action table-action-preview"
                                  id={`tooltip-preview-${data.id}`}
                                  onClick={(e) => onPreviewSurvey(data.id, data.status)}>
                                  <i className="fas fa-eye" />
                                </button>
                                <UncontrolledTooltip
                                  delay={0}
                                  target={`tooltip-preview-${data.id}`}>
                                  Preview Template
                                </UncontrolledTooltip>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    <CardFooter className="py-4">
                      <Pagination
                        totalRow={totalGatherTemplatesRow}
                        perPage={perPage}
                        initialPage={pageGatherTemplates}
                        onPageChange={(data) => {
                          setPageGatherTemplates(data.selected);
                        }}
                      />
                    </CardFooter>
                  </>
                )}
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default SelectTemplate;
